/**
 * 고정 프로그램 목록의 원본은 연간시간표 셀이다.
 * 셀 LS·React Query 캐시를 먼저 보여주고, 없을 때만 연간시간표와 같은 조회로 채운다.
 * 그 캐시가 있으면 연간시간표 페이지는 같은 키를 다시 받지 않는다.
 */
import { queryClient } from '../query/queryClient';
import { annualTimetableCellsQueryOptions } from '../query/hooks/useAnnualTimetableCellsQuery';
import { listCachedAnnualTimetableCells } from '../services/annualTimetableService';
import {
  getClassScheduleSettings,
  getProgramColorSettings,
  loadClassScheduleSettingsCache,
  loadProgramColorSettingsCache,
  type ClassScheduleRow,
  type ClassScheduleSettings,
  type ProgramColorSettings,
} from '../services/sectionLayoutService';
import type { AnnualTimetableCell } from '../types/domain';
import {
  dateIsoForMondayWeekday,
  isAnnualShortDayTailMemo,
} from './annualTimetable';
import { parseAnnualCellKey } from './annualTimetable';
import {
  DEFAULT_FIXED_TIMETABLE_NAME,
  draftApplySig,
  draftPeriodName,
  fixedDraftCompareSig,
  loadFixedDrafts,
  saveFixedDrafts,
  scheduleRowsForGrade,
  type FixedProgramDraft,
} from './fixedProgramDrafts';

const sigMemo = new Map<string, string>();
const ensureInflight = new Map<string, Promise<void>>();

function periodKeyFromSlot(rows: ClassScheduleRow[], slotIndex: number): string {
  let classCount = 0;
  let afterSchoolCount = 0;
  for (let i = 0; i <= slotIndex && i < rows.length; i += 1) {
    const kind = rows[i]?.kind;
    if (kind === 'class') classCount += 1;
    else if (kind === 'after_school') afterSchoolCount += 1;
  }
  const kind = rows[slotIndex]?.kind;
  if (kind === 'class' && classCount > 0) return `class:${classCount}`;
  if (kind === 'after_school' && afterSchoolCount > 0) return `after_school:${afterSchoolCount}`;
  return `raw:${slotIndex}`;
}

function sortPeriods(keys: string[]): string[] {
  const rank = (key: string) => {
    if (key.startsWith('class:')) return Number(key.slice(6)) || 0;
    if (key.startsWith('after_school:')) return 100 + (Number(key.slice(13)) || 0);
    if (key.startsWith('raw:')) return 200 + (Number(key.slice(4)) || 0);
    return 999;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

function fixedPalette(settings: ProgramColorSettings | null): Set<string> {
  const set = new Set<string>();
  const add = (list?: readonly string[]) => {
    list?.forEach((c) => {
      const n = (c ?? '').trim().toLowerCase();
      if (n) set.add(n);
    });
  };
  if (!settings) return set;
  add(settings.workspaceFallback?.regular);
  Object.values(settings.colorsByGrade ?? {}).forEach((grade) => add(grade.regular));
  return set;
}

function cellSetSig(cells: AnnualTimetableCell[]): string {
  let h = 2166136261;
  let n = 0;
  cells.forEach((cell) => {
    const memo = (cell.memo ?? '').trim();
    if (!memo || cell.slot_kind === 'out_of_term') return;
    n += 1;
    const token = `${cell.grade_id}|${cell.monday_iso}|${cell.weekday_key}|${cell.slot_index}|${memo}|${cell.color_hex ?? ''}|${cell.text_value ?? ''}|${cell.updated_at ?? ''}`;
    for (let i = 0; i < token.length; i += 1) {
      h ^= token.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
  });
  return `${n}:${h >>> 0}`;
}

function cellDraftId(date: string, name: string): string {
  return `cell_${date}_${name.replace(/\s+/g, '_').slice(0, 40)}`;
}

/** 연간시간표 셀 → 고정 프로그램 초안(고정 팔레트 색만) */
export function draftsFromAnnualCells(
  cells: AnnualTimetableCell[],
  schedule: ClassScheduleSettings | null,
  colors: ProgramColorSettings | null
): FixedProgramDraft[] {
  const palette = fixedPalette(colors);
  if (palette.size === 0) return [];
  type Group = {
    date: string;
    name: string;
    color: string;
    grades: Set<string>;
    periods: Set<string>;
    textCount: Record<string, Map<string, number>>;
  };
  const grouped = new Map<string, Group>();
  cells.forEach((cell) => {
    const name = (cell.memo ?? '').trim();
    if (!name || isAnnualShortDayTailMemo(name) || cell.slot_kind === 'out_of_term') return;
    const color = (cell.color_hex ?? '').trim();
    if (!palette.has(color.toLowerCase())) return;
    const date = dateIsoForMondayWeekday(cell.monday_iso, cell.weekday_key);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    const period = periodKeyFromSlot(scheduleRowsForGrade(schedule, cell.grade_id), cell.slot_index);
    const groupKey = `${date}|${name}|${color.toLowerCase()}`;
    const group = grouped.get(groupKey) ?? {
      date,
      name,
      color,
      grades: new Set<string>(),
      periods: new Set<string>(),
      textCount: {},
    };
    group.grades.add(cell.grade_id);
    group.periods.add(period);
    const text = (cell.text_value ?? '').trim();
    const bucket = group.textCount[period] ?? new Map<string, number>();
    bucket.set(text, (bucket.get(text) ?? 0) + 1);
    group.textCount[period] = bucket;
    grouped.set(groupKey, group);
  });
  const now = Date.now();
  return Array.from(grouped.values()).map((group) => {
    const periods = sortPeriods(Array.from(group.periods));
    const timetableNames: Record<string, string> = {};
    periods.forEach((pk) => {
      const best = Array.from(group.textCount[pk]?.entries() ?? []).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
      if (best) timetableNames[pk] = best;
    });
    const counts = new Map<string, number>();
    Object.values(timetableNames).forEach((text) => counts.set(text, (counts.get(text) ?? 0) + 1));
    const top = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
    return {
      id: cellDraftId(group.date, group.name),
      date: group.date,
      name: group.name,
      periods,
      timetableName: top === DEFAULT_FIXED_TIMETABLE_NAME ? '' : top,
      timetableNames,
      colorHex: group.color,
      startTime: '',
      endTime: '',
      gradeIds: Array.from(group.grades),
      source: 'cell',
      updatedAt: now,
    };
  });
}

function applySeed(
  existing: FixedProgramDraft[],
  seeded: FixedProgramDraft[],
  insertOnly: boolean
): FixedProgramDraft[] | null {
  const byId = new Map(existing.map((d) => [d.id, d]));
  let changed = false;
  seeded.forEach((seed) => {
    const prev = byId.get(seed.id);
    if (prev?.deleted || (prev && (prev.source !== 'cell' || insertOnly))) return;
    const next: FixedProgramDraft = prev
      ? {
          ...prev,
          date: seed.date,
          name: seed.name,
          periods: seed.periods,
          timetableName: seed.timetableName,
          timetableNames: seed.timetableNames,
          colorHex: seed.colorHex,
          gradeIds: seed.gradeIds,
          source: 'cell',
          updatedAt: prev.updatedAt,
        }
      : seed;
    if (prev && fixedDraftCompareSig(prev) === fixedDraftCompareSig(next)) return;
    if (prev) next.updatedAt = Date.now();
    byId.set(seed.id, next);
    changed = true;
  });
  return changed ? Array.from(byId.values()) : null;
}

/** 연간시간표에 실제로 있는 고정 프로그램(등록 모달이 셀에 넣은 값) */
export type FixedCellSnapshot = {
  dateIso: string;
  eventName: string;
  colorHex: string;
  gradeIds: string[];
  periodKeys: string[];
  timetableByPeriodKey: Record<string, string>;
  cellKeys: string[];
};

function isSlotTargetKey(key: string): boolean {
  const [kind, orderRaw] = key.split(':');
  if (kind !== 'class' && kind !== 'after_school') return false;
  const order = Number(orderRaw);
  return Number.isInteger(order) && order >= 1;
}

/**
 * 시간표 셀에 있는 고정 프로그램을 관리 목록에 맞춘다.
 * 등록 모달로 넣은 행사(목록에 없거나, 예전에 지운 뒤 셀에 다시 있는 행사)도 같은 목록으로 들어온다.
 * skipCellKeys 에 있는 칸은 이번 턴에 목록→셀로 다시 쓰는 중이라, 아직 옛 셀 값으로 목록을 덮지 않는다.
 */
export function mergeFixedDraftsFromCellSnapshots(
  drafts: FixedProgramDraft[],
  snapshots: FixedCellSnapshot[],
  opts: { pageGradeIds: string[]; skipCellKeys?: ReadonlySet<string>; liveCellKeys?: ReadonlySet<string> }
): { drafts: FixedProgramDraft[]; changed: boolean } {
  const pageGrades = new Set(opts.pageGradeIds);
  const skip = opts.skipCellKeys ?? new Set<string>();
  const visible = snapshots.filter(
    (sm) => sm.dateIso && sm.eventName.trim() && sm.cellKeys.some((key) => !skip.has(key))
  );
  const owner = new Map<string, string>();
  drafts.forEach((d) => {
    if (d.deleted) return;
    Object.values(d.applied ?? {}).forEach((a) => a.cellKeys.forEach((key) => owner.set(key, d.id)));
  });
  const summaryCellSet = new Set(visible.flatMap((sm) => sm.cellKeys));
  const byId = new Map(drafts.map((d) => [d.id, d] as const));
  let changed = false;
  const put = (d: FixedProgramDraft) => {
    byId.set(d.id, d);
    changed = true;
  };
  const cellsByGradeOf = (keys: string[]) => {
    const grouped: Record<string, string[]> = {};
    keys.forEach((key) => {
      const parsed = parseAnnualCellKey(key);
      if (parsed && pageGrades.has(parsed.gradeId)) (grouped[parsed.gradeId] ||= []).push(key);
    });
    return grouped;
  };
  const topText = (sm: FixedCellSnapshot) => {
    const counts = new Map<string, number>();
    Object.values(sm.timetableByPeriodKey).forEach((text) => text && counts.set(text, (counts.get(text) ?? 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  };
  const namesOf = (sm: FixedCellSnapshot, periods: string[]) => {
    const names: Record<string, string> = {};
    periods.forEach((key) => {
      const text = (sm.timetableByPeriodKey[key] ?? '').trim();
      if (text) names[key] = text;
    });
    return names;
  };
  const now = Date.now();
  visible.forEach((sm) => {
    if (sm.cellKeys.some((key) => skip.has(key))) return;
    const periods = sm.periodKeys.filter(isSlotTargetKey);
    const smGrades = sm.gradeIds.filter((id) => pageGrades.has(id));
    if (!sm.dateIso || !sm.eventName.trim() || smGrades.length === 0) return;
    const byGrade = cellsByGradeOf(sm.cellKeys);
    const ownerIds = Array.from(new Set(sm.cellKeys.map((key) => owner.get(key)).filter((id): id is string => !!id)));
    const tt = topText(sm);
    const id = `cell_${sm.dateIso}_${sm.eventName.trim().replace(/\s+/g, '_').slice(0, 40)}`;
    if (ownerIds.length === 0) {
      const ex = byId.get(id);
      if (ex && ex.source !== 'cell' && !ex.deleted) return;
      const kept = ex && !ex.deleted ? ex : null;
      const merged: FixedProgramDraft = {
        id,
        date: sm.dateIso,
        name: sm.eventName.trim(),
        periods,
        hours: kept?.hours && kept.hours === periods.length ? kept.hours : undefined,
        timetableName: tt === DEFAULT_FIXED_TIMETABLE_NAME ? '' : tt,
        timetableNames: namesOf(sm, periods),
        colorHex: sm.colorHex,
        startTime: kept?.startTime ?? '',
        endTime: kept?.endTime ?? '',
        gradeIds: Array.from(new Set([...(kept?.gradeIds ?? []).filter((gradeId) => !pageGrades.has(gradeId)), ...smGrades])),
        source: 'cell',
        updatedAt: now,
        deleted: false,
      };
      const sig = draftApplySig(merged);
      const applied = { ...(merged.applied ?? {}) };
      Object.entries(byGrade).forEach(([gradeId, keys]) => {
        applied[gradeId] = { sig, cellKeys: keys };
      });
      const next = { ...merged, applied };
      const appliedSame =
        !!ex &&
        !ex.deleted &&
        smGrades.every(
          (gradeId) =>
            JSON.stringify([...(ex.applied?.[gradeId]?.cellKeys ?? [])].sort()) ===
            JSON.stringify([...(applied[gradeId]?.cellKeys ?? [])].sort())
        );
      if (ex && !ex.deleted && appliedSame && fixedDraftCompareSig(ex) === fixedDraftCompareSig(next)) return;
      put(next);
      return;
    }
    const current = byId.get(ownerIds[0]);
    if (!current || current.deleted) return;
    const sigNow = draftApplySig(current);
    const inSync = Object.entries(current.applied ?? {}).every(([gradeId, applied]) => !pageGrades.has(gradeId) || applied.sig === sigNow);
    if (!inSync) return;
    const nextPeriods = periods.length ? periods : current.periods;
    const smNames = namesOf(sm, nextPeriods);
    const namesSame = nextPeriods.every((key) => !smNames[key] || smNames[key] === draftPeriodName(current, key));
    const nextNames = namesSame ? current.timetableNames : { ...(current.timetableNames ?? {}), ...smNames };
    const nextColor = (sm.colorHex || '').trim();
    const colorSame = !nextColor || nextColor.toLowerCase() === (current.colorHex || '').trim().toLowerCase();
    const nextGrades = Array.from(new Set([...current.gradeIds.filter((id) => !pageGrades.has(id)), ...smGrades]));
    const same =
      current.date === sm.dateIso &&
      current.name.trim() === sm.eventName.trim() &&
      JSON.stringify([...current.periods].sort()) === JSON.stringify([...nextPeriods].sort()) &&
      namesSame &&
      colorSame &&
      JSON.stringify([...current.gradeIds].sort()) === JSON.stringify([...nextGrades].sort());
    const appliedSame = Object.entries(byGrade).every(
      ([gradeId, keys]) => JSON.stringify([...(current.applied?.[gradeId]?.cellKeys ?? [])].sort()) === JSON.stringify([...keys].sort())
    );
    if (same && appliedSame) return;
    const updated: FixedProgramDraft = {
      ...current,
      date: sm.dateIso,
      name: sm.eventName.trim(),
      periods: nextPeriods,
      hours: current.hours && current.hours !== nextPeriods.length ? undefined : current.hours,
      timetableNames: nextNames,
      colorHex: colorSame ? current.colorHex : nextColor,
      gradeIds: nextGrades,
      updatedAt: now,
    };
    const sig = draftApplySig(updated);
    const applied: NonNullable<FixedProgramDraft['applied']> = {};
    Object.entries(current.applied ?? {}).forEach(([gradeId, item]) => {
      if (!pageGrades.has(gradeId)) applied[gradeId] = item;
    });
    Object.entries(byGrade).forEach(([gradeId, keys]) => {
      applied[gradeId] = { sig, cellKeys: keys };
    });
    put({ ...updated, applied });
  });
  Array.from(byId.values()).forEach((draft) => {
    if (!opts.liveCellKeys || draft.deleted || !draft.applied) return;
    const sigNow = draftApplySig(draft);
    let gone: string[] = [];
    Object.entries(draft.applied).forEach(([gradeId, applied]) => {
      if (!pageGrades.has(gradeId) || applied.sig !== sigNow || applied.cellKeys.length === 0) return;
      if (applied.cellKeys.some((key) => skip.has(key))) return;
      const known = applied.cellKeys.filter((key) => opts.liveCellKeys!.has(key));
      if (known.length === 0) return;
      if (known.every((key) => !summaryCellSet.has(key))) gone = [...gone, gradeId];
    });
    if (gone.length === 0) return;
    const applied = { ...draft.applied };
    gone.forEach((gradeId) => delete applied[gradeId]);
    const gradeIds = draft.gradeIds.filter((id) => !gone.includes(id));
    put({ ...draft, applied, gradeIds, deleted: gradeIds.length === 0 ? true : draft.deleted, updatedAt: now });
  });
  return { drafts: Array.from(byId.values()), changed };
}

/** 이미 받아 둔 연간시간표 셀로 고정 프로그램 목록을 채운다. 같으면 저장하지 않는다. */
export function seedFixedDraftsFromAnnualCells(
  workspaceId: string,
  cells: AnnualTimetableCell[],
  opts?: { insertOnly?: boolean }
): boolean {
  const sid = workspaceId.trim();
  if (!sid || cells.length === 0) return false;
  const colors = loadProgramColorSettingsCache(sid);
  const schedule = loadClassScheduleSettingsCache(sid);
  if (!colors || !schedule) return false;
  const scheduleSig = schedule.sections
    .map((section) => `${section.id}:${(schedule.sectionRows[section.id] ?? []).map((row) => row.kind).join('')}`)
    .join('|');
  const sig = `${cellSetSig(cells)}|${Array.from(fixedPalette(colors)).sort().join(',')}|${scheduleSig}`;
  if (sigMemo.get(sid) === sig) return false;
  const seeded = draftsFromAnnualCells(cells, schedule, colors);
  sigMemo.set(sid, sig);
  if (seeded.length === 0) return false;
  const next = applySeed(loadFixedDrafts(sid), seeded, !!opts?.insertOnly);
  if (!next) return false;
  saveFixedDrafts(sid, next);
  return true;
}

/** 네트워크 없이, 이 워크스페이스 연간시간표 셀 캐시로 목록을 채운다. */
export function seedFixedDraftsFromCachedAnnualCells(workspaceId: string, academicYear?: number): boolean {
  const sid = workspaceId.trim();
  if (!sid) return false;
  return seedFixedDraftsFromAnnualCells(sid, listCachedAnnualTimetableCells(sid, academicYear));
}

/**
 * 학사일정 등 연간시간표 화면 밖에서 고정 프로그램을 열 때.
 * 셀 캐시가 있으면 그대로 쓰고, 없으면 연간시간표와 같은 쿼리로 받아 캐시에 둔다.
 */
export function ensureFixedProgramsFromAnnualTimetable(params: {
  workspaceId: string;
  academicYear: number;
  gradeIds: string[];
}): Promise<void> {
  const sid = params.workspaceId.trim();
  const year = params.academicYear;
  const gradeIds = Array.from(new Set(params.gradeIds.map((id) => id.trim()).filter(Boolean)));
  if (!sid) return Promise.resolve();
  seedFixedDraftsFromCachedAnnualCells(sid);
  if (!year || gradeIds.length === 0) return Promise.resolve();
  const key = `${sid}|${year}|${gradeIds.slice().sort().join(',')}`;
  const pending = ensureInflight.get(key);
  if (pending) return pending;
  const job = (async () => {
    await Promise.all([
      getClassScheduleSettings(sid).catch(() => null),
      getProgramColorSettings(sid).catch(() => null),
    ]);
    const lists = await Promise.all(
      gradeIds.flatMap((gradeId) =>
        ([1, 2] as const).map((termNumber) => {
          const options = annualTimetableCellsQueryOptions({
            workspaceId: sid,
            academicYear: year,
            termNumber,
            gradeId,
            enabled: true,
          });
          return queryClient.fetchQuery({
            queryKey: options.queryKey,
            queryFn: options.queryFn,
          });
        })
      )
    );
    const cells = lists.flat().filter((row): row is AnnualTimetableCell => !!row && typeof row === 'object' && 'grade_id' in row);
    seedFixedDraftsFromAnnualCells(sid, cells);
  })().finally(() => {
    if (ensureInflight.get(key) === job) ensureInflight.delete(key);
  });
  ensureInflight.set(key, job);
  return job;
}
